import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import {
  ArcUser,
  deleteUser,
  fetchUsers,
  getLoggedInUsername,
  reAuthenticate,
} from "lib/arcAuthApi";
import AddUserDialog from "./AddUserDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";

const INPUT_CLASS =
  "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none";

const UserManagementPanel: React.FC = () => {
  const { t } = useTranslation(["settings", "common"]);
  const [unlocked, setUnlocked] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const [users, setUsers] = useState<ArcUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loggedInUsername = getLoggedInUsername();
  const currentUserIsDefault =
    users.find((u) => u.username === loggedInUsername)?.isDefault ?? false;

  const [addDialog, setAddDialog] = useState(false);
  const [changePasswordTarget, setChangePasswordTarget] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setUsers(await fetchUsers());
    } catch {
      setLoadError(t("userManagement.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (unlocked) {
      void loadUsers();
    }
  }, [unlocked, loadUsers]);

  const handleUnlock = useCallback(async () => {
    const username = getLoggedInUsername();
    if (!username) {
      setUnlockError(t("userManagement.cannotDetermineUser"));
      return;
    }
    setUnlocking(true);
    setUnlockError(null);
    try {
      const ok = await reAuthenticate(username, unlockPassword);
      if (ok) {
        setUnlocked(true);
      } else {
        setUnlockError(t("userManagement.incorrectPassword"));
      }
    } catch {
      setUnlockError(t("userManagement.authUnavailable"));
    } finally {
      setUnlocking(false);
    }
  }, [unlockPassword, t]);

  const handleDelete = useCallback(
    async (username: string) => {
      try {
        await deleteUser(username);
        await loadUsers();
      } catch (e) {
        setLoadError(toUserFacingApiError(e, t("userManagement.deleteFailed")));
      }
    },
    [loadUsers, t]
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-black80 pb-4">
        <p className="text-h4 font-semibold text-white">{t("userManagement.title")}</p>
      </div>

      {!unlocked ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <p className="text-h5 text-black40">{t("userManagement.enterPassword")}</p>
          <input
            type="password"
            value={unlockPassword}
            onChange={(e) => setUnlockPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void handleUnlock(); }}
            autoFocus
            autoComplete="current-password"
            className={`${INPUT_CLASS} max-w-xs`}
          />
          {unlockError ? (
            <p className="text-h5 text-red-400" role="alert">
              {unlockError}
            </p>
          ) : null}
          <FunctionalButton
            className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 hover:bg-primary1 disabled:opacity-50"
            title={unlocking ? t("common:unlocking") : t("common:unlock")}
            titleClassName="!normal-case text-h5 font-semibold text-white"
            onClick={() => void handleUnlock()}
            disabled={unlocking}
          />
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-h5 font-semibold text-white">{t("userManagement.users")}</p>
            <FunctionalButton
              className="rounded-r5 border border-primary2 bg-primary2 px-3 py-1.5 hover:bg-primary1"
              title={t("userManagement.addUser")}
              titleClassName="!normal-case text-h5 font-semibold text-white"
              onClick={() => setAddDialog(true)}
            />
          </div>

          {loading ? (
            <p className="text-h5 text-black40">{t("common:loading")}</p>
          ) : loadError ? (
            <div className="flex items-center gap-3">
              <p className="text-h5 text-red-400">{loadError}</p>
              <button
                className="text-h5 text-primary2 underline"
                onClick={() => void loadUsers()}
              >
                {t("common:retry")}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {users.map((user) => (
                <div
                  key={user.username}
                  className="flex items-center justify-between rounded-r5 border border-black80 bg-black90 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-h5 text-white">{user.username}</span>
                    {user.isDefault ? (
                      <span className="rounded-r5 bg-primary2 px-2 py-0.5 text-h5 text-white">
                        {t("userManagement.defaultUser")}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      title={
                        !currentUserIsDefault && user.username !== loggedInUsername
                          ? t("userManagement.onlyDefaultCanChangeOthers")
                          : undefined
                      }
                    >
                      <FunctionalButton
                        className={`rounded-r5 border border-black80 px-3 py-1.5 ${
                          !currentUserIsDefault && user.username !== loggedInUsername
                            ? "cursor-not-allowed bg-black70 opacity-50"
                            : "bg-black70 hover:bg-black80"
                        }`}
                        title={t("userManagement.changePassword")}
                        titleClassName="!normal-case text-h5 font-semibold text-white"
                        onClick={() => {
                          if (currentUserIsDefault || user.username === loggedInUsername) {
                            setChangePasswordTarget(user.username);
                          }
                        }}
                        disabled={!currentUserIsDefault && user.username !== loggedInUsername}
                      />
                    </span>
                    <span
                      title={user.isDefault ? t("userManagement.defaultCannotDelete") : undefined}
                    >
                      <FunctionalButton
                        className={`rounded-r5 border border-black80 px-3 py-1.5 ${
                          user.isDefault
                            ? "cursor-not-allowed bg-black70 opacity-50"
                            : "bg-black70 hover:bg-black80"
                        }`}
                        title={t("common:delete")}
                        titleClassName="!normal-case text-h5 font-semibold text-red-400"
                        onClick={() => !user.isDefault && void handleDelete(user.username)}
                        disabled={user.isDefault}
                      />
                    </span>
                  </div>
                </div>
              ))}
              {users.length === 0 ? (
                <p className="text-h5 text-black40">{t("userManagement.noUsersFound")}</p>
              ) : null}
            </div>
          )}
        </div>
      )}

      <AddUserDialog
        visible={addDialog}
        onClose={() => setAddDialog(false)}
        onSaved={() => void loadUsers()}
      />
      <ChangePasswordDialog
        visible={changePasswordTarget !== null}
        username={changePasswordTarget}
        isSelf={changePasswordTarget === loggedInUsername}
        onClose={() => setChangePasswordTarget(null)}
        onSaved={() => void loadUsers()}
      />
    </div>
  );
};

export default UserManagementPanel;
